import { createActionCreators } from 'immer-reducer';
import { push } from 'connected-react-router';

import TokensLocalStorage from '@/utils/local-storage/TokensLocalStorage';

import { LoginReducer } from '@/store/reducers/auth';
import { ChangePasswordBody } from '@/api/main-protected';

import { changeLanguageLocal } from '@/store/actions/language';
import { clearApiCache } from '@/pwa/init';
import { forgetResponses, revalidate } from '@/store/responses';
import { AsyncAction } from './common';
import { userActions } from './auth';

export const loginActions = createActionCreators(LoginReducer);

export type LoginActions =
  | ReturnType<typeof loginActions.setIsPending>
  | ReturnType<typeof loginActions.setIsResolved>
  | ReturnType<typeof loginActions.setIsSaveLoading>
  | ReturnType<typeof loginActions.setIsChangePasswordRejected>
  | ReturnType<typeof loginActions.setIsRejected>
  | ReturnType<typeof loginActions.setIsAvailable>;

export const login = (data: {email: string, password: string}, path?: string): AsyncAction => async (
  dispatch,
  _,
  { mainApi },
) => {
  try {
    dispatch(loginActions.setIsPending());

    const storage = TokensLocalStorage.getInstance();

    const response = await mainApi.login(data);
    const { accessToken, refreshToken } = response;

    storage.setAccessToken(accessToken);
    storage.setRefreshToken(refreshToken);

    // Awaited before the app is marked logged in: both caches are shared per
    // origin, so a second user on this browser would otherwise be shown the
    // previous user's rows until revalidation lands.
    await Promise.all([clearApiCache(), forgetResponses()]);

    dispatch(userActions.setIsLoggedIn(true));

    dispatch(getMe(false, path));
  } catch (e) {
    dispatch(loginActions.setIsRejected());
  }
};

export const getMe = (isGlobal: boolean = false, path = '/me'): AsyncAction => async (
  dispatch,
  getState,
  { mainProtectedApi },
) => {
  try {
    const storage = TokensLocalStorage.getInstance();

    const accessToken = storage.getAccessToken();

    if (!accessToken) {
      dispatch(loginActions.setIsRejected());
      return;
    }

    await revalidate('/users/profile', () => mainProtectedApi.getMe(), (user) => {
      if (user.language) dispatch(changeLanguageLocal(user.language));

      dispatch(userActions.setRole(user.role));
      dispatch(userActions.setMe(user));
      dispatch(loginActions.setIsResolved());
      dispatch(loginActions.setIsAvailable(true));
    }, { slot: 'userReducer.user' });

    if (!isGlobal) dispatch(push(path));
  } catch (e) {
    console.log(e);
    dispatch(loginActions.setIsRejected());
    dispatch(loginActions.setIsAvailable(false));
  }
};

export const changePassword = (body: ChangePasswordBody): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    dispatch(loginActions.setIsSaveLoading(true));

    await mainProtectedApi.changePassword(body);

    dispatch(push('/me'));
  } catch (e) {
    dispatch(loginActions.setIsChangePasswordRejected());
  } finally {
    dispatch(loginActions.setIsSaveLoading(false));
  }
};

export const logout = (): AsyncAction => async (dispatch) => {
  dispatch(userActions.setIsLoggedIn(false));
  dispatch(loginActions.setIsRejected());
  const storage = TokensLocalStorage.getInstance();
  storage.clear();

  // Not awaited -- navigation must not wait on storage, and the clear is
  // idempotent if it lands after the route change. forgetResponses() drops the
  // in-memory copies synchronously, before the push below.
  clearApiCache();
  forgetResponses();

  dispatch(push('/login'));
};
